<template>
  <div>
    <reports-title
      ref="reportsTitle"
      :title="pageTitle"
    />
    <reports-toolbar
      ref="reportsToolbar"
      :drop-menu="modelsOfCalculationsMenu"
      :report-title="pageTitle"
      :toolbar-disabled="isStatic || isDisabled"
      @onSelectDropdown="onChangeCalculationMode"
      @onClickExport="onExport"
    />
    <skeleton-wrapper
      v-if="isShowSkeleton"
      id="skeletonWrapper"
      :style="{ height: contentHeight }"
    >
      <template #body>
        <bar-chart
          id="report_export_id"
          :config="{}"
          :stub-config="stubConfig"
          :is-static="isStatic"
          :is-disabled="true"
          :show-stub-message="false"
        />
      </template>
    </skeleton-wrapper>
    <bar-chart
      v-else
      id="report_export_id"
      :config="chartConfig"
      :stub-config="stubConfig"
      :is-static="isStatic"
      :is-disabled="isDisabled"
      :is-single-project="currentProjectIds.length === 1"
      :show-stub-message="true"
      :chart-name="chartName"
      :legend-items="currentProjectIds.length > 1 ? legendItems : []"
      @toggleChart="toggleData"
    />
  </div>
</template>

<script>
import multiViewsProjects from '$$multiviewModel';
import constants from '$$helpers/constants';
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import barChart from '../include/charts/negativeBarChart.vue';
// import moduleGanttDateWorker from '../../gantt/modules/ganttDateWorker';
import Progress from '../progress/controllerReportsProgress';
import charts from '../progress/configCharts';
import baselinesModel from '../../../models/baselines';
import projectsModel from '$$projectModel';
import reportsConstants from '../include/constants';
import skeletonWrapper from '../../common/VueComponents/skeletons/skeletonWrapper.vue';
import statusHelper from '../../../helpers/status';
import reportHelpers from '../include/helpers';
import Export from '../include/export/export';

export default {
  name: 'ScheduleProgressReportPage',
  components: {
    reportsTitle, reportsToolbar, barChart, skeletonWrapper,
  },
  data() {
    return {
      locale: __,
      projectIds: [],
      multiviewProjectIds: [],
      baselines: [],
      baselineData: null,
      baselineId: null,
      chartConfig: {},
      stubConfig: {},
      calculationMode: constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED,
      clientWidth: 0,
      isPositiveProgressDisabled: false,
      isNegativeProgressDisabled: false,
      isStatic: false,
      isDisabled: false,
      chartName: '',
      isShowSkeleton: true,
      contentHeight: '0px',
    };
  },
  computed: {
    pageTitle() {
      return reportsConstants.TITLE.progress.schedule;
    },
    modelsOfCalculationsMenu() {
      return reportHelpers.getModelsOfCalculationsMenu(this.isStatic, this.calculationMode, this.baselines, this.baselineId);
    },
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    legendItems() {
      return [
        {
          name: 'positive',
          label: __('report_progress_schedule_positive'),
          color: this.isPositiveProgressDisabled || this.isStatic ? '#E9E9E9' : '#8BC34A',
          disabled: this.isPositiveProgressDisabled || this.isStatic,
        },
        {
          name: 'negative',
          label: __('report_progress_schedule_negative'),
          color: this.isNegativeProgressDisabled || this.isStatic ? '#CCCCCC' : '#EE7950',
          disabled: this.isNegativeProgressDisabled || this.isStatic,
        },
      ];
    },
    filterProjectIds() {
      return [this.projectIds, this.multiviewProjectIds].find(arr => arr.length) || [];
    },
    currentProjectIds() {
      if (!this.filterProjectIds.length) {
        return projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      return this.filterProjectIds;
    },
    sourceType() {
      if (this.multiviewProjectIds.length) {
        return 'multiview';
      }

      return 'projects';
    },
    baselineEnabledByCalculationMode() {
      const baselineEnabledByCalculationMode = [
        constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE, constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
      ].includes(this.calculationMode)

      return baselineEnabledByCalculationMode;
    }
  },
  watch: {
    'currentFilter.properties.gantt_id': function (projectIds) {
      if (projectIds?.length) {
        this.projectIds = projectIds;
      } else {
        this.projectIds = [];
      }
    },
    'currentFilter.properties.portfolio_id': function (portfolioIds) {
      if (portfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(portfolioIds[0]);

        this.multiviewProjectIds = multiviewData.ganttIDs;
      } else {
        this.multiviewProjectIds = [];
      }
    },
    currentProjectIds(projectIds) {
      if (projectIds && projectIds.length) {
        this.initReportByFilter();
      }
    },
    leftSidebarWidth(width) {
      this.clientWidth = `${document.documentElement.clientWidth - width}px`;
    },
    filterProjectIds(ids) {
      if (!ids.length) {
        this.isNegativeProgressDisabled = false;
        this.isPositiveProgressDisabled = false;
        this.isDisabled = false;
      }
    },
    calculationMode(newVal) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          calculationModel: newVal,
        },
      });
    },
  },
  created() {
    this.chartConfig = charts.scheduleProgressChart.stub;
    this.stubConfig = charts.scheduleProgressChart.stub;
  },
  mounted() {
    this.clientWidth = `${document.documentElement.clientWidth - this.leftSidebarWidth}px`;
    this.init();

    userExtAnalytics.log('report_open', {
      report_type: this.$route?.params?.type,
      source_type: this.sourceType,
      projects_selected_count: this.currentProjectIds.length,
      calculation_mode: this.calculationMode,
    });
    this.calcContentHeight();
    window.addEventListener('resize', this.calcContentHeight);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calcContentHeight);
  },
  methods: {
    calcContentHeight() {
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight || 0;

      const titleRef = this.$refs.reportsTitle;
      const toolbarRef = this.$refs.reportsToolbar;

      const titleEl = titleRef && (titleRef.$el || titleRef);
      const toolbarEl = toolbarRef && (toolbarRef.$el || toolbarRef);

      const titleHeight = titleEl?.getBoundingClientRect().height || 0;
      const toolbarHeight = toolbarEl?.getBoundingClientRect().height || 0;

      const height = Math.max(viewportHeight - titleHeight - toolbarHeight - 90, 0);

      this.contentHeight = `${height}px`;
    },
    init() {
      this.calculationMode = this.$route?.query?.calculationModel || constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;

      if (this.currentFilter?.properties?.gantt_id?.length) {
        this.projectIds = this.currentFilter.properties.gantt_id;
      }

      if (this.currentFilter?.properties?.portfolio_id?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter?.properties?.portfolio_id[0]);

        if (multiviewData) {
          this.multiviewProjectIds = multiviewData.ganttIDs;
        }
      }

      if (!this.filterProjectIds.length) {
        this.projectIds = projectsModel.getAllProjects().map(project => project.gantt_id);
      }
    },

    async initReportByFilter() {
      await this.getBaselines();
      this.updateFilterSettingsAndCalculationMode();
      this.calcProgressReport();
    },

    async getBaselines() {
      this.baselineId = null;
      this.baselines = [];
      this.baselineData = {};

      if (this.currentProjectIds.length === 1) {
        await this.$store.dispatch('baselines/fetchBaselines', this.currentProjectIds[0]);

        const baselines = this.$store.getters['baselines/getProjectBaselines'](this.currentProjectIds[0])
          .map(item => ({ ...item, created_at: new Date(item.created_at) }))
          .sort((a, b) => a.created_at - b.created_at);

        if (baselines.length) {
          this.baselines = baselines;
          this.baselineId = baselines[baselines.length - 1].id;
          await this.getBaselineData(this.baselineId);
        }
      }
    },
    async getBaselineData(baselineId) {
      const baseline = this.baselines.find(b => b.id === +baselineId);

      if (!baseline) {
        this.baselineData = {};

        return
      }

      const data = await this.$store.dispatch('baselines/getBaselineData', { projectId: baseline.gantt_id, eventId: baseline.event_id });

      if (data) {
        this.baselineData = data;
        this.baselineId = baselineId;
      }
    },
    async calcProgressReport() {
      const result = await Progress.calculateScheduleReport({
        projectIds: this.currentProjectIds,
        calculationMode: this.calculationMode,
        baselineData: this.baselineData,
      });

      if (result) {
        this.chartConfig = { ...result.config };
        this.allData = result.config?.series[0]?.data;
        this.isStatic = false;

        if (this.currentProjectIds.length === 1) {
          const projectData = projectsModel.getProjectDataById(this.currentProjectIds[0]);

          this.chartName = projectData.name;
        } else {
          this.setDataBySelectedLegendItem();
          this.chartName = '';
        }
      } else {
        this.chartName = '';
        this.isStatic = true;
        this.isNegativeProgressDisabled = false;
        this.isPositiveProgressDisabled = false;
      }
      setTimeout(() => {
        this.isShowSkeleton = false;
      }, 500)
      statusHelper.handlers.hideMainSpinner();
    },
    async onChangeCalculationMode(selected) {
      const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

      userExtAnalytics.log('report_calculation_mode_change', {
        report_type: reportNameAnalytics,
        from: this.calculationMode,
        to: selected.type,
      });

      this.calculationMode = selected.type;
      statusHelper.handlers.showMainSpinner();

      let baselineEnabled = false;

      if (selected?.baselineId) {
        await this.getBaselineData(selected.baselineId);
        baselineEnabled = true;
      } else {
        this.baselineId = null;
      }

      this.calcProgressReport();
      this.updateFilterAdvancedSettings({
        baselineEnabled: baselineEnabled,
      })
    },
    toggleData(name) {
      if (name === 'positive') {
        this.isPositiveProgressDisabled = !this.isPositiveProgressDisabled;
      }

      if (name === 'negative') {
        this.isNegativeProgressDisabled = !this.isNegativeProgressDisabled;
      }

      this.setDataBySelectedLegendItem();
    },
    setDataBySelectedLegendItem() {
      const filteredData = this.allData.filter(item => {
        if (!this.isPositiveProgressDisabled && item.value > 0) {
          return item;
        }

        if (!this.isNegativeProgressDisabled && item.value < 0) {
          return item;
        }
      });

      if (!filteredData.length) {
        this.isDisabled = true;

        return;
      }

      const filteredConfig = _.cloneDeep(this.chartConfig);

      filteredConfig.yAxis.data = filteredData.map(item => item.progressData.projectName);
      filteredConfig.series[0].data = filteredData;

      this.isDisabled = false;
      this.chartConfig = { ...filteredConfig };
    },
    onExport() {
      Export.exportReportGraphicToPdf({
        title: __(`${this.pageTitle}`),
      });

      userExtAnalytics.log('report_export_done', {
        export_format: 'PDF',
        report_type: 'report_progress_schedule',
      });
    },
    updateFilterSettingsAndCalculationMode() {
      if (!this.baselineEnabledByCalculationMode && this.currentProjectIds.length > 1) {
        this.updateFilterAdvancedSettings({
          isShowNotificationMessage: false,
        })
      }

      if (this.baselineEnabledByCalculationMode && this.currentProjectIds.length > 1) {
        this.resetCalculationMode();
        this.updateFilterAdvancedSettings();
      }

      if (this.baselineEnabledByCalculationMode && !this.baselines.length) {
        this.resetCalculationMode();
        this.updateFilterAdvancedSettings({
          baselineEnabled: false,
          isShowNotificationMessage: true,
        })
      }

      if (this.baselineEnabledByCalculationMode) {
        this.updateFilterAdvancedSettings({
          baselineEnabled: true,
          isShowNotificationMessage: false,
        })
      }
    },

    updateFilterAdvancedSettings(settings = {}) {
      const defaultFilterSettings = {
        type: this.currentFilter.type,
        settings: {
          baselineEnabled: false,
          isShowNotificationMessage: false,
        }
      }

      this.$store.commit('filter/updateAdvancedSettingsByFilterType', {
        ...defaultFilterSettings,
        settings: {
          ...defaultFilterSettings.settings,
          ...settings
        }
      });
    },

    resetCalculationMode() {
      this.calculationMode = constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED;
    }
  },
};
</script>
